import {
  ArrowLeft,
  KeyRound,
  ListPlus,
  MoreHorizontal,
  Plus,
  Power,
  PowerOff,
  Send,
  Settings2,
  Trash2,
} from "lucide-react";
import * as React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Page, PageHeader } from "@/components/layout/page";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState, ErrorState, Spinner, TableSkeleton } from "@/components/ui/feedback";
import { CopyButton } from "@/components/ui/misc";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { WebhookDeliveriesCard, WebhookDeliveryDrawer } from "@/features/webhook-deliveries";
import { WebhookEndpointFormDialog } from "@/features/webhook-endpoint-form";
import { WebhookSecretDialog, type ShownSecret } from "@/features/webhook-secret-dialog";
import { WebhookSubscriptionFormDialog } from "@/features/webhook-subscription-form";
import { useCallAlerts } from "@/lib/slack";
import { ApiError } from "@/lib/api";
import {
  useCampaigns,
  useDeleteWebhookEndpoint,
  useDeleteWebhookSubscription,
  useRotateWebhookSecret,
  useSendWebhookTest,
  useUpdateWebhookEndpoint,
  useUpdateWebhookSubscription,
  useWebhookEndpoint,
  useWebhookSubscriptions,
} from "@/lib/queries";
import type { WebhookEndpoint, WebhookSubscription } from "@/lib/types";
import { cn } from "@/lib/utils";
import { conditionsText, eventTerm } from "@/lib/webhooks";

/**
 * One webhook endpoint (ADR-022): what it hears, a test of each, and what was sent to it.
 *
 * A test is the way to set a receiver up before a real event happens. It sends the event's sample
 * once, marked as a test, and opens the delivery, which follows its answer: a Zapier catch hook that
 * got one shows the fields to map, and one that 404s says the URL is wrong.
 */
export function WebhookPage() {
  const { endpointId } = useParams();
  const id = Number(endpointId);
  const valid = Number.isInteger(id) && id > 0;
  const navigate = useNavigate();
  const toast = useToast();
  const endpoint = useWebhookEndpoint(valid ? id : undefined);
  const subscriptions = useWebhookSubscriptions(valid ? id : undefined);
  const campaigns = useCampaigns();
  const update = useUpdateWebhookEndpoint();
  const remove = useDeleteWebhookEndpoint();
  const rotate = useRotateWebhookSecret();
  const updateSubscription = useUpdateWebhookSubscription();
  const removeSubscription = useDeleteWebhookSubscription();
  const sendTest = useSendWebhookTest();
  const callAlerts = useCallAlerts();

  const [editing, setEditing] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [rotating, setRotating] = React.useState(false);
  const [secret, setSecret] = React.useState<ShownSecret>();
  const [form, setForm] = React.useState<{
    open: boolean;
    subscription?: WebhookSubscription;
  }>({ open: false });
  const [removing, setRemoving] = React.useState<WebhookSubscription>();
  const [testing, setTesting] = React.useState<number>();
  const [delivery, setDelivery] = React.useState<number>();

  if (valid && endpoint.isPending) {
    return (
      <Page>
        <div className="flex min-h-[40vh] items-center justify-center">
          <Spinner className="size-5" />
        </div>
      </Page>
    );
  }

  if (!valid || endpoint.error || !endpoint.data) {
    const missing = !valid || (endpoint.error instanceof ApiError && endpoint.error.status === 404);
    return (
      <Page>
        {missing ? (
          <EmptyState
            className="min-h-[60vh]"
            title="Endpoint not found"
            description="It may have been deleted, or it belongs to another organisation."
            action={
              <Button variant="outline" asChild>
                <Link to="/webhooks">Back to webhooks</Link>
              </Button>
            }
          />
        ) : (
          <ErrorState
            className="min-h-[60vh]"
            error={endpoint.error}
            title="Could not load the endpoint"
            action={
              <Button variant="outline" onClick={() => void endpoint.refetch()}>
                Try again
              </Button>
            }
          />
        )}
      </Page>
    );
  }

  const e: WebhookEndpoint = endpoint.data;
  const campaignNames = new Map((campaigns.data ?? []).map((c) => [c.id, c.name]));
  const headerCount = Object.keys(e.headers).length;

  const fail = (title: string) => (err: unknown) =>
    toast.error(title, err instanceof ApiError ? err.message : "Please try again.");

  const setOn = async (enabled: boolean) => {
    try {
      await update.mutateAsync({ id: e.id, enabled });
      toast.success(enabled ? "Endpoint on" : "Endpoint off", e.name);
    } catch (err) {
      fail(enabled ? "Could not turn it on" : "Could not turn it off")(err);
    }
  };

  const setSubscriptionOn = async (subscription: WebhookSubscription, enabled: boolean) => {
    try {
      await updateSubscription.mutateAsync({
        endpointId: e.id,
        id: subscription.id,
        enabled,
      });
      toast.success(enabled ? "Event on" : "Event off", eventTerm(subscription.event_type).label);
    } catch (err) {
      fail(enabled ? "Could not turn it on" : "Could not turn it off")(err);
    }
  };

  const test = async (subscription: WebhookSubscription) => {
    setTesting(subscription.id);
    try {
      const queued = await sendTest.mutateAsync({
        endpointId: e.id,
        id: subscription.id,
      });
      setDelivery(queued.id);
    } catch (err) {
      fail("Could not send a test")(err);
    } finally {
      setTesting(undefined);
    }
  };

  const addButton = (
    <div className="flex gap-2">
      {e.kind === "slack" && (
        <Button
          variant="outline"
          size="sm"
          loading={callAlerts.isPending}
          onClick={() =>
            callAlerts.mutate(e.id, {
              onError: fail("Could not add call alerts"),
            })
          }
        >
          Add call alerts
        </Button>
      )}
      <Button variant="outline" size="sm" onClick={() => setForm({ open: true })}>
        <Plus />
        Add an event
      </Button>
    </div>
  );

  return (
    <Page>
      <Link
        to="/webhooks"
        className="mb-3 inline-flex items-center gap-1 text-xs text-text-muted hover:text-text"
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        Webhooks
      </Link>

      <PageHeader
        title={
          <span className="inline-flex flex-wrap items-center gap-2">
            {e.name}
            {!e.enabled && <Badge tone="warning">Off</Badge>}
          </span>
        }
        description={
          <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="inline-flex min-w-0 items-center gap-1 font-mono text-xs">
              <span className="text-text-subtle">{e.kind === "slack" ? "Slack" : e.method}</span>
              <span className="break-all">
                {e.kind === "slack" ? `#${e.slack_channel_name ?? e.slack_channel_id}` : e.url}
              </span>
              {e.url && <CopyButton value={e.url} label="Copy the URL" />}
            </span>
            {headerCount > 0 && <span>{headerCount === 1 ? "1 header" : `${headerCount} headers`}</span>}
          </span>
        }
        action={
          <>
            <Button variant="outline" onClick={() => setEditing(true)}>
              <Settings2 />
              Edit
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="More actions">
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {e.enabled ? (
                  <DropdownMenuItem onSelect={() => void setOn(false)}>
                    <PowerOff />
                    Turn off
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onSelect={() => void setOn(true)}>
                    <Power />
                    Turn on
                  </DropdownMenuItem>
                )}
                {e.kind !== "slack" && (
                  <DropdownMenuItem onSelect={() => setRotating(true)}>
                    <KeyRound />
                    Rotate the secret
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem destructive onSelect={() => setDeleting(true)}>
                  <Trash2 />
                  Delete endpoint
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
      />

      <div className="space-y-4">
        <Card className="overflow-hidden">
          <CardHeader
            title="Events"
            description="What this endpoint hears. Send a test to show a receiver what an event looks like before a real one happens."
            action={addButton}
          />
          {subscriptions.isPending ? (
            <TableSkeleton rows={2} cols={5} />
          ) : subscriptions.error ? (
            <ErrorState
              error={subscriptions.error}
              title="Could not load the events"
              action={
                <Button variant="outline" onClick={() => void subscriptions.refetch()}>
                  Try again
                </Button>
              }
            />
          ) : subscriptions.data.length === 0 ? (
            <EmptyState
              icon={ListPlus}
              title="No events yet"
              description="Add the event this endpoint should hear, then send a test. Until an event is added, nothing is sent here."
              action={addButton}
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Event</TableHead>
                  <TableHead>From</TableHead>
                  <TableHead>Only when</TableHead>
                  <TableHead>{e.kind === "slack" ? "Message" : "Body"}</TableHead>
                  {e.kind === "slack" && <TableHead>Post in</TableHead>}
                  <TableHead className="w-48" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {subscriptions.data.map((s) => {
                  const term = eventTerm(s.event_type);
                  const conditions = conditionsText(s.conditions);
                  return (
                    <TableRow key={s.id} className="[&>td]:h-12">
                      <TableCell>
                        <span className="inline-flex flex-wrap items-center gap-1.5">
                          <span className={cn("font-medium", s.enabled ? "text-text" : "text-text-muted")}>
                            {term.label}
                          </span>
                          {!s.enabled && (
                            <Badge tone="outline" size="sm">
                              Off
                            </Badge>
                          )}
                          {!term.sent && (
                            <Badge tone="warning" size="sm">
                              Not sent yet
                            </Badge>
                          )}
                        </span>
                        <p className="font-mono text-xs text-text-muted">{s.event_type}</p>
                      </TableCell>
                      <TableCell className="text-xs text-text">
                        {s.source_type === "campaign"
                          ? (campaignNames.get(s.source_id ?? "") ?? "A campaign that is gone")
                          : "Every campaign"}
                      </TableCell>
                      <TableCell className="max-w-xs text-xs text-text-muted">
                        {conditions ?? "Always"}
                      </TableCell>
                      <TableCell className="text-xs text-text">
                        {s.body_mode === "template"
                          ? "A template"
                          : s.body_mode === "message"
                            ? "Built-in message"
                            : "The event"}
                      </TableCell>
                      {e.kind === "slack" && (
                        <TableCell className="text-xs">
                          {s.post_in === "channel" ? "Channel" : "Thread"}
                        </TableCell>
                      )}
                      <TableCell>
                        <span className="flex items-center justify-end gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={!s.enabled || !e.enabled}
                            title={
                              !e.enabled
                                ? "Turn the endpoint on to send a test"
                                : !s.enabled
                                  ? "Turn the event on to send a test"
                                  : undefined
                            }
                            loading={testing === s.id}
                            onClick={() => void test(s)}
                          >
                            <Send />
                            Send a test
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${term.label}`}>
                                <MoreHorizontal />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onSelect={() => setForm({ open: true, subscription: s })}>
                                <Settings2 />
                                Edit event
                              </DropdownMenuItem>
                              {s.enabled ? (
                                <DropdownMenuItem onSelect={() => void setSubscriptionOn(s, false)}>
                                  <PowerOff />
                                  Turn off
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onSelect={() => void setSubscriptionOn(s, true)}>
                                  <Power />
                                  Turn on
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem destructive onSelect={() => setRemoving(s)}>
                                <Trash2 />
                                Remove
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Card>

        <WebhookDeliveriesCard endpointId={e.id} />
      </div>

      <WebhookEndpointFormDialog open={editing} onOpenChange={setEditing} endpoint={e} />

      <WebhookSubscriptionFormDialog
        open={form.open}
        onOpenChange={(open) => setForm((current) => ({ ...current, open }))}
        endpoint={e}
        subscription={form.subscription}
      />

      <WebhookDeliveryDrawer deliveryId={delivery} onClose={() => setDelivery(undefined)} />

      <WebhookSecretDialog secret={secret} onClose={() => setSecret(undefined)} />

      <ConfirmDialog
        open={rotating}
        onOpenChange={setRotating}
        title="Rotate the signing secret?"
        description={
          <>
            Deliveries to <span className="font-medium">{e.name}</span> are signed with a new secret from the
            moment it is made. A receiver still checking the old one refuses them until it has the new one. A
            Zapier catch hook does not check it.
          </>
        }
        confirmLabel="Rotate secret"
        loading={rotate.isPending}
        onConfirm={async () => {
          try {
            const rotated = await rotate.mutateAsync(e.id);
            setRotating(false);
            setSecret({
              endpointId: e.id,
              name: e.name,
              secret: rotated.secret,
              rotated: true,
            });
          } catch (err) {
            fail("Could not rotate the secret")(err);
          }
        }}
      />

      <ConfirmDialog
        open={Boolean(removing)}
        onOpenChange={(open) => !open && setRemoving(undefined)}
        title="Remove this event?"
        description={
          <>
            <span className="font-medium">{removing ? eventTerm(removing.event_type).label : ""}</span> stops
            going to {e.name}, and what was sent for it leaves the log too. Turning it off instead keeps the
            log.
          </>
        }
        confirmLabel="Remove event"
        destructive
        loading={removeSubscription.isPending}
        onConfirm={async () => {
          if (!removing) return;
          try {
            await removeSubscription.mutateAsync({
              endpointId: e.id,
              id: removing.id,
            });
            toast.success("Event removed", eventTerm(removing.event_type).label);
            setRemoving(undefined);
          } catch (err) {
            fail("Could not remove the event")(err);
          }
        }}
      />

      <ConfirmDialog
        open={deleting}
        onOpenChange={setDeleting}
        title="Delete this endpoint?"
        description={
          <>
            <span className="font-medium">{e.name}</span>, its events and everything sent through them will be
            removed, and its receiver stops hearing from the platform. Turning it off instead keeps its log.
          </>
        }
        confirmLabel="Delete endpoint"
        destructive
        loading={remove.isPending}
        onConfirm={async () => {
          try {
            await remove.mutateAsync(e.id);
            toast.success("Endpoint deleted", e.name);
            navigate("/webhooks", { replace: true });
          } catch (err) {
            fail("Could not delete the endpoint")(err);
          }
        }}
      />
    </Page>
  );
}
